import { useCallback, useEffect, useState } from "react";

import { useDropzone } from "react-dropzone";

import {
  AlertCircle,
  CheckCircle2,
  Download,
  Loader2,
  Upload,
  X,
} from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useAccountImport } from "~/hooks/contabilidad/useAccountImport";
import { useAccountsByCompany } from "~/hooks/contabilidad/useAccounts";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { downloadAccountTemplate } from "~/lib/contabilidad/generateAccountTemplate";
import {
  type AccountParseResult,
  hasAccountValidationErrors,
  hasImportableLines,
  parseAccountFile,
} from "~/lib/contabilidad/parseAccountFile";
import { cn } from "~/lib/utils";
import { ACCOUNT_TYPE_COLORS, ACCOUNT_TYPE_LABELS } from "~/types/contabilidad";

interface CuentaImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CuentaImportDialog({
  open,
  onOpenChange,
  onSuccess,
}: CuentaImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<AccountParseResult | null>(
    null
  );
  const [isParsing, setIsParsing] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");

  const { data: companies = [], isLoading: isLoadingCompanies } =
    useCompanies();
  const { data: existingAccounts = [] } =
    useAccountsByCompany(selectedCompanyId);
  const importMutation = useAccountImport();

  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);

  // Re-parse file when company changes (existing accounts are different)
  useEffect(() => {
    if (file && selectedCompanyId && existingAccounts) {
      handleParseFile(file);
    } else if (!selectedCompanyId) {
      setParseResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, existingAccounts]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setFile(null);
      setParseResult(null);
      setSelectedCompanyId("");
    }
  }, [open]);

  const handleParseFile = async (uploadedFile: File) => {
    if (!selectedCompanyId) {
      return;
    }

    setIsParsing(true);
    try {
      const result = await parseAccountFile(uploadedFile, existingAccounts);
      setParseResult(result);
    } catch (error) {
      console.error("Error parsing file:", error);
      setParseResult({
        lines: [],
        errors: ["Error al procesar el archivo"],
        summary: { toCreate: 0, alreadyExists: 0, invalid: 0 },
      });
    } finally {
      setIsParsing(false);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const uploadedFile = acceptedFiles[0];
      if (uploadedFile) {
        setFile(uploadedFile);
        if (selectedCompanyId) {
          handleParseFile(uploadedFile);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCompanyId, existingAccounts]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    maxFiles: 1,
    disabled: !selectedCompanyId,
  });

  const handleRemoveFile = () => {
    setFile(null);
    setParseResult(null);
  };

  const handleSubmit = () => {
    if (
      !parseResult ||
      !selectedCompanyId ||
      !hasImportableLines(parseResult)
    ) {
      return;
    }

    // Get valid lines (no errors)
    const validLines = parseResult.lines.filter((line) => !line.error);

    importMutation.mutate(
      {
        companyId: selectedCompanyId,
        accounts: validLines.map((line) => ({
          code: line.code,
          name: line.name,
          account_type: line.accountType,
          is_active: line.isActive,
        })),
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
      }
    );
  };

  const canSubmit =
    parseResult &&
    !hasAccountValidationErrors(parseResult) &&
    hasImportableLines(parseResult);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Importar Cuentas</DialogTitle>
          <DialogDescription>
            Cargue un archivo Excel o CSV con las cuentas a crear.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Company Selection */}
          <div className="space-y-2">
            <Label>Empresa *</Label>
            <Select
              value={selectedCompanyId}
              onValueChange={setSelectedCompanyId}
              disabled={isLoadingCompanies}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Seleccione una empresa" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Template Download & File Upload */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Archivo de Datos</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => downloadAccountTemplate(selectedCompany?.name)}
                className="h-8 rounded-lg"
              >
                <Download className="mr-2 h-4 w-4" />
                Descargar Plantilla
              </Button>
            </div>

            {!file ? (
              <div
                {...getRootProps()}
                className={cn(
                  "cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors",
                  isDragActive
                    ? "border-primary bg-primary/5"
                    : "border-muted-foreground/25 hover:border-primary/50",
                  !selectedCompanyId && "cursor-not-allowed opacity-50"
                )}
              >
                <input {...getInputProps()} />
                <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">
                  {!selectedCompanyId
                    ? "Primero seleccione una empresa"
                    : isDragActive
                      ? "Suelte el archivo aquí..."
                      : "Arrastre un archivo aquí o haga clic para seleccionar"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Formatos permitidos: .xlsx, .xls, .csv
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-xl border bg-muted/50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleRemoveFile}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Parsing indicator */}
          {isParsing && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm text-muted-foreground">
                Procesando archivo...
              </span>
            </div>
          )}

          {/* Global Errors */}
          {parseResult && parseResult.errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-inside list-disc">
                  {parseResult.errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Preview Table */}
          {parseResult && parseResult.lines.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Vista Previa ({parseResult.lines.length} filas)</Label>
                <div className="flex gap-3 text-sm">
                  {parseResult.summary.toCreate > 0 && (
                    <span className="text-green-600">
                      {parseResult.summary.toCreate} a crear
                    </span>
                  )}
                  {parseResult.summary.alreadyExists > 0 && (
                    <span className="text-amber-600">
                      {parseResult.summary.alreadyExists} ya existen
                    </span>
                  )}
                  {parseResult.summary.invalid > 0 && (
                    <span className="text-destructive">
                      {parseResult.summary.invalid} inválidas
                    </span>
                  )}
                </div>
              </div>
              <div className="max-h-[300px] overflow-auto rounded-xl border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">Fila</TableHead>
                      <TableHead className="w-[100px]">Codigo</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead className="w-[100px]">Tipo</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parseResult.lines.map((line, idx) => (
                      <TableRow
                        key={idx}
                        className={cn(
                          "duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both",
                          line.error && "bg-destructive/10",
                          line.existsInDb && !line.error && "bg-amber-50"
                        )}
                        style={{
                          animationDelay: `${Math.min(idx * 40, 400)}ms`,
                        }}
                      >
                        <TableCell className="text-muted-foreground">
                          {line.rowNumber}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {line.code}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {line.name}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={cn(
                              "text-xs",
                              ACCOUNT_TYPE_COLORS[line.accountType]
                            )}
                          >
                            {ACCOUNT_TYPE_LABELS[line.accountType]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {line.error && (
                            <AlertCircle
                              className="h-4 w-4 text-destructive"
                              title={line.error}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={5}>
                        <div className="flex gap-4 text-sm">
                          <span className="text-green-600">
                            {parseResult.summary.toCreate} a crear
                          </span>
                          {parseResult.summary.alreadyExists > 0 && (
                            <span className="text-amber-600">
                              {parseResult.summary.alreadyExists} ya existen
                              (serán omitidas)
                            </span>
                          )}
                          {parseResult.summary.invalid > 0 && (
                            <span className="text-destructive">
                              {parseResult.summary.invalid} con errores
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>

              {/* Line errors */}
              {parseResult.lines.some((l) => l.error) && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-inside list-disc">
                      {parseResult.lines
                        .filter((l) => l.error)
                        .slice(0, 5)
                        .map((line, idx) => (
                          <li key={idx}>
                            Fila {line.rowNumber}: {line.error}
                          </li>
                        ))}
                      {parseResult.lines.filter((l) => l.error).length > 5 && (
                        <li>
                          ...y{" "}
                          {parseResult.lines.filter((l) => l.error).length - 5}{" "}
                          errores más
                        </li>
                      )}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="rounded-full px-6"
              disabled={importMutation.isPending || !canSubmit}
              onClick={handleSubmit}
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importando...
                </>
              ) : (
                `Importar ${parseResult?.summary.toCreate ?? 0} Cuentas`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
